import React from "react";
import { Download, Copy, ExternalLink } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { supabase } from "@/integrations/supabase/client";

interface ImageActionButtonsProps {
  imageUrl: string;
  unsplashData?: {
    id: string;
    photographer: string;
    photographerUsername: string;
    photographerUrl: string;
    unsplashUrl: string;
    downloadUrl: string;
    downloadEndpoint: string;
    description: string;
  };
}

const ImageActionButtons: React.FC<ImageActionButtonsProps> = ({ imageUrl, unsplashData }) => {
  const { toast } = useToast();

  const getPicsumId = (url: string): string | null => {
    // Extract ID from various Picsum URL formats
    const patterns = [
      /picsum\.photos\/id\/(\d+)/,
      /picsum\.photos\/(\d+)/,
      /\/id\/(\d+)/
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  };

  const getOriginalImageUrl = async (url: string): Promise<string> => {
    const picsumId = getPicsumId(url);
    if (!picsumId) return url;

    try {
      const response = await fetch(`https://picsum.photos/id/${picsumId}/info`);
      const data = await response.json();
      return data.download_url || url;
    } catch (error) {
      console.error('Failed to get original URL:', error);
      return url;
    }
  };

  const handleDownload = async () => {
    try {
      let downloadUrl = imageUrl;
      let filename = `target-image-${Date.now()}.jpg`;

      if (unsplashData) {
        // For Unsplash images, trigger the download event first
        const { error: fnError } = await supabase.functions.invoke('unsplash-download', {
          body: { downloadEndpoint: unsplashData.downloadEndpoint },
        });

        if (fnError) {
          throw new Error('Failed to trigger download event');
        }

        // Use the high-resolution download URL
        downloadUrl = unsplashData.downloadUrl;
        filename = `unsplash-${unsplashData.id}.jpg`;
      } else {
        // For other images, extract filename from URL
        const urlParts = imageUrl.split('/');
        const urlFilename = urlParts[urlParts.length - 1];
        if (urlFilename && urlFilename.includes('.')) {
          filename = urlFilename;
        }
      }

      // Fetch the image and create a blob for proper download
      const response = await fetch(downloadUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch image: ${response.statusText}`);
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      
      // Create download link
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the blob URL
      window.URL.revokeObjectURL(url);
      
      toast({
        title: "Download started",
        description: "The target image is being downloaded to your device.",
      });
    } catch (error) {
      console.error('Download failed:', error);
      toast({
        title: "Download failed",
        description: "Unable to download the image. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleCopyUrl = async () => {
    try {
      const urlToCopy = unsplashData ? unsplashData.unsplashUrl : imageUrl;
      await navigator.clipboard.writeText(urlToCopy);
      toast({
        title: "URL copied",
        description: unsplashData ? "The Unsplash page URL has been copied to your clipboard." : "The image URL has been copied to your clipboard.",
      });
    } catch (error) {
      console.error('Copy failed:', error);
      toast({
        title: "Copy failed",
        description: "Failed to copy URL to clipboard. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleOpenExternal = async () => {
    try {
      const urlToOpen = unsplashData ? unsplashData.unsplashUrl : imageUrl;
      window.open(urlToOpen, '_blank', 'noopener,noreferrer');
      toast({
        title: "Opening page",
        description: unsplashData ? "The Unsplash photo page is opening in a new tab." : "The image is opening in a new tab.",
      });
    } catch (error) {
      console.error('Failed to open URL:', error);
      toast({
        title: "Error",
        description: "Failed to open the page. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <TooltipProvider>
      <div className="flex gap-2 mt-2 justify-end">
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className="w-8 h-8 rounded-full flex justify-center items-center hover:bg-accent transition-colors"
              onClick={handleDownload}
              aria-label="Download target image"
            >
              <Download className="h-4 w-4 text-muted-foreground" />
            </button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Download image</p>
          </TooltipContent>
        </Tooltip>
        
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className="w-8 h-8 rounded-full flex justify-center items-center hover:bg-accent transition-colors"
              onClick={handleCopyUrl}
              aria-label="Copy image URL"
            >
              <Copy className="h-4 w-4 text-muted-foreground" />
            </button>
          </TooltipTrigger>
          <TooltipContent>
            <p>{unsplashData ? "Copy Unsplash page URL" : "Copy image URL"}</p>
          </TooltipContent>
        </Tooltip>
        
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className="w-8 h-8 rounded-full flex justify-center items-center hover:bg-accent transition-colors"
              onClick={handleOpenExternal}
              aria-label="Open image in new tab"
            >
              <ExternalLink className="h-4 w-4 text-muted-foreground" />
            </button>
          </TooltipTrigger>
          <TooltipContent>
            <p>{unsplashData ? "Open Unsplash page" : "Open in new tab"}</p>
          </TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  );
};

export default ImageActionButtons;